import React, {
  forwardRef,
  useRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
  PolarRadiusAxis,
} from "recharts";
import variables from "../../../../../themes/base/styles/variables";
import html2canvas from "html2canvas";

export const RadarGraphStyles = {
  firstPartStyles: {
    fontSize: variables.fontSizeLarge,
    fontWeight: variables.fontWeightBold,
    fontFamily: variables.fontFamilyBold,
  },
  remainingPartsStyles: {
    fontWeight: variables.fontWeightNormal,
    fontFamily: variables.fontFamilyRegular,
    fontSize: variables.fontSizeSmall,
  },
};

const Graph = forwardRef(
  (
    {
      data,
      width,
      height,
      cx,
      cy,
      outerRadius,
      gridType,
      dataKey1,
      dataKey2,
      stroke,
      fill,
      fillOpacity,
      linkLabel,
    },
    ref
  ) => {
    const chartRef = useRef(null);
    const [ , setContainerSize] = useState({ width: 0, height: 0 });
    useEffect(() => {
      if (chartRef.current) {
        setContainerSize({
          width: chartRef.current.offsetWidth,
          height: chartRef.current.offsetHeight,
        });
      }
    }, []);

    const CustomizedAxisTick = ({ x, y, payload, cx:customCX, cy:customCY }) => {
      const { value } = payload;
      let splitPayload = value.split(" ");
      let firstPart = splitPayload.shift();
      let remainingParts = splitPayload.join(" ");

      // Determine text alignment for top and bottom sections
      const isTopSection = y < customCY;
      const textVerticalOffset = isTopSection ? -30 : 20; // Adjust the offset as needed

      return (
        <g transform={`translate(${x + (x - customCX) / 2.5}, ${y})`}>
          <text
            textAnchor="middle"
            fill={variables.charade}
            y={textVerticalOffset} // Use y attribute for vertical positioning
            style={RadarGraphStyles.firstPartStyles}
          >
            {firstPart}
          </text>
          <text
            textAnchor="middle"
            fill={variables.charade}
            y={textVerticalOffset + 20} // Position the remaining parts below the firstPart
            style={RadarGraphStyles.remainingPartsStyles}
          >
            {remainingParts}
          </text>
        </g>
      );
    };

    const downloadChartImage = useCallback(() => {
      const node = chartRef.current;
      // Call the downloadChartImage function of the Graph component only if it is mounted
      try {
        if (node) {
          html2canvas(node).then((canvas) => {
            const link = document.createElement("a");
            link.download = linkLabel;
            link.href = canvas.toDataURL();
            link.click();
          });
        }
      } catch (error) {}
    }, [chartRef]);

    ref.current = {
      downloadChartImage,
    };

    return (
      <div ref={chartRef} style={{ width: width }}>
        <ResponsiveContainer width={width} height={height}>
          <RadarChart cx={cx} cy={cy} data={data} outerRadius={outerRadius}>
            <PolarGrid gridType={gridType} />
            <PolarAngleAxis dataKey={dataKey1} tick={CustomizedAxisTick} />
            <PolarRadiusAxis />
            <Radar
              dataKey={dataKey2}
              stroke={stroke}
              fill={fill}
              fillOpacity={fillOpacity}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    );
  }
);

export default Graph;
